import { formatMoney } from './money-formatting.js';
import { fetchConfig } from './utilities.js';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} SellingPlanAllocation
 * @property {number} sellingPlanId
 * @property {string} name
 * @property {string} [optionName]
 * @property {number} price
 * @property {number|null} compareAtPrice
 * @property {number|null} [perDeliveryPrice]
 */

/**
 * @typedef {object} Offer
 * @property {number} productId
 * @property {string} productHandle
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {string|null} featuredImage
 * @property {string|null} [visualImage]
 * @property {number} variantId
 * @property {string} variantTitle
 * @property {string} title
 * @property {string} label
 * @property {string} badge
 * @property {string} subtitle
 * @property {number} units
 * @property {string} duration
 * @property {string} description
 * @property {boolean} featured
 * @property {string} shippingLabel
 * @property {boolean} available
 * @property {number} price
 * @property {number|null} compareAtPrice
 * @property {string} [checkoutUrl]
 * @property {SellingPlanAllocation[]} sellingPlanAllocations
 */

/**
 * @typedef {object} InitialSubscriptionOffer
 * @property {number} productId
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {string|null} featuredImage
 * @property {number} variantId
 * @property {string} variantTitle
 * @property {boolean} available
 * @property {number} price
 * @property {number|null} compareAtPrice
 * @property {number} selectedSellingPlanId
 * @property {string} billingInterval
 * @property {number} billingIntervalCount
 * @property {string} deliveryInterval
 * @property {number} deliveryIntervalCount
 * @property {SellingPlanAllocation[]} sellingPlanAllocations
 */

/**
 * @typedef {object} PurchaseState
 * @property {'one_time'|'subscription'} purchaseMode
 * @property {number} selectedOfferIndex
 * @property {number} selectedProductId
 * @property {number} selectedVariantId
 * @property {number|null} selectedSellingPlanId
 * @property {number} selectedUnits
 * @property {boolean} available
 * @property {number} price
 * @property {number|null} compareAtPrice
 */

/**
 * @typedef {object} ExternalCheckoutConfig
 * @property {boolean} enabled
 * @property {string} subscriptionUrl
 */

class PurencyProductPurchase extends HTMLElement {
  /** @type {Offer[]} */
  #offers = [];

  /** @type {InitialSubscriptionOffer|null} */
  #initialSubscriptionOffer = null;

  /** @type {'subscription'|'one_time'} */
  #initialInternalMode = 'one_time';

  /** @type {PurchaseState} */
  #state = {
    purchaseMode: 'one_time',
    selectedOfferIndex: 0,
    selectedProductId: 0,
    selectedVariantId: 0,
    selectedSellingPlanId: null,
    selectedUnits: 1,
    available: false,
    price: 0,
    compareAtPrice: null,
  };

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'BRL';

  /** @type {string} */
  #defaultMode = 'subscription';

  /** @type {string} */
  #shippingFallback = '';

  /** @type {string} */
  #kitItemLabel = '';

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {number|null} */
  #initialOfferIndex = null;

  /** @type {number|null} */
  #initialTreatmentIndex = null;

  /** @type {string} */
  #subscriptionDisclaimer = '';

  /** @type {string} */
  #initialSubscriptionVisualImage = '';

  /** @type {ExternalCheckoutConfig} */
  #externalCheckout = {
    enabled: false,
    subscriptionUrl: '',
  };

  /** @type {{ src: string; srcset: string }|null} */
  #productMediaOriginal = null;

  /** @type {MediaQueryList|null} */
  #mobileMediaQuery = null;

  static #BODY_SUBSCRIPTION_CLASS = 'purency-initial-subscription';
  static #BODY_ONETIME_CLASS = 'purency-initial-onetime';

  /** @type {(() => void)|null} */
  #stickyScrollHandler = null;

  static #PAGE_BODY_CLASS = 'purency-purchase-page';
  static #STICKER_BODY_CLASS = 'purency-sticker-visible';

  connectedCallback() {
    try {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      this.#parseConfig();
      if (!this.#offers.length) {
        if (this.dataset.designMode === 'true') {
          console.warn('[purency-product-purchase] No offers found in configuration payload.');
        }
        return;
      }

      this.#initializeState();
      this.#bindEvents();
      this.#applyPageLayout();
      this.#render();
      this.#setupStickyCta();
    } catch (error) {
      console.error('[purency-product-purchase] Failed to initialize', error);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#removePageLayout();
    this.#clearPresentationState();
    document.body.classList.remove(PurencyProductPurchase.#STICKER_BODY_CLASS);
  }

  #parseConfig() {
    const dataEl = this.querySelector('[data-purency-purchase-json]');
    if (!(dataEl instanceof HTMLScriptElement)) return;

    try {
      const payload = JSON.parse(dataEl.textContent || '{}');
      this.#offers = Array.isArray(payload.offers) ? payload.offers : [];
      this.#moneyFormat = payload.moneyFormat || this.#moneyFormat;
      this.#currency = payload.currency || this.#currency;
      this.#defaultMode = payload.defaultPurchaseMode === 'one_time' ? 'one_time' : 'subscription';
      this.#shippingFallback = payload.shippingFallback || '';
      const parsedInitial =
        typeof payload.initialOfferIndex === 'number' ? payload.initialOfferIndex : Number(payload.initialOfferIndex);
      this.#initialOfferIndex = Number.isInteger(parsedInitial) ? parsedInitial : null;
      const parsedTreatment =
        typeof payload.initialTreatmentIndex === 'number'
          ? payload.initialTreatmentIndex
          : Number(payload.initialTreatmentIndex);
      this.#initialTreatmentIndex = Number.isInteger(parsedTreatment) ? parsedTreatment : null;
      this.#kitItemLabel = typeof payload.kitItemLabel === 'string' ? payload.kitItemLabel : '';
      this.#subscriptionDisclaimer =
        typeof payload.subscriptionDisclaimer === 'string' ? payload.subscriptionDisclaimer : '';
      this.#initialSubscriptionVisualImage =
        typeof payload.initialSubscriptionVisualImage === 'string'
          ? payload.initialSubscriptionVisualImage
          : '';
      this.#initialSubscriptionOffer =
        payload.initialSubscriptionOffer && typeof payload.initialSubscriptionOffer === 'object'
          ? payload.initialSubscriptionOffer
          : null;
      const externalCheckout = payload.externalCheckout;
      const subscriptionUrl =
        typeof externalCheckout?.subscriptionUrl === 'string' ? externalCheckout.subscriptionUrl.trim() : '';
      const hasOfferUrls = this.#offers.some(
        (offer) => typeof offer.checkoutUrl === 'string' && offer.checkoutUrl.trim().length > 0
      );
      const explicitEnabled = Boolean(externalCheckout?.enabled) || this.dataset.externalCheckout === 'true';
      this.#externalCheckout = {
        enabled: explicitEnabled || hasOfferUrls || Boolean(subscriptionUrl),
        subscriptionUrl,
      };

      if (
        this.dataset.designMode === 'true' &&
        payload.initialSubscriptionOffer === null &&
        this.querySelector('[data-purency-purchase-json]')
      ) {
        const hasSetting = Boolean(this.closest('[data-block-id]'));
        if (hasSetting) {
          console.warn(
            '[purency-product-purchase] initial_subscription_product is empty or unavailable.'
          );
        }
      }
    } catch (error) {
      console.error('[purency-product-purchase] Invalid JSON payload', error);
      this.#offers = [];
      this.#initialSubscriptionOffer = null;
    }
  }

  #initializeState() {
    const featuredIndex = this.#resolveInitialOfferIndex();
    const offer = this.#offers[featuredIndex] || this.#offers[0];
    if (!offer) return;

    this.#initialInternalMode = 'one_time';
    this.#state.purchaseMode = 'one_time';
    this.#state.selectedOfferIndex = featuredIndex;
    this.#applyOffer(offer);
  }

  #resolveInitialOfferIndex() {
    const configured = Number(this.dataset.featuredOfferPosition || '2') - 1;
    const unitByPosition = [6, 3, 1];
    const targetUnits = unitByPosition[configured] ?? 3;
    const byUnits = this.#offers.findIndex(
      (offer) => offer.available && this.#offerUnits(offer) === targetUnits
    );
    if (byUnits >= 0) return byUnits;

    const fromPayload = this.#initialOfferIndex;
    if (
      Number.isInteger(fromPayload) &&
      fromPayload >= 0 &&
      fromPayload < this.#offers.length &&
      this.#offers[fromPayload]?.available
    ) {
      return fromPayload;
    }

    const fromDom = Number(this.dataset.initialOfferIndex);
    if (
      Number.isInteger(fromDom) &&
      fromDom >= 0 &&
      fromDom < this.#offers.length &&
      this.#offers[fromDom]?.available
    ) {
      return fromDom;
    }

    const featured = this.#offers.findIndex((offer) => offer.featured && offer.available);
    if (featured >= 0) return featured;

    if (configured >= 0 && configured < this.#offers.length && this.#offers[configured]?.available) {
      return configured;
    }

    const firstAvailable = this.#offers.findIndex((offer) => offer.available);
    if (firstAvailable >= 0) return firstAvailable;

    return 0;
  }

  /** @param {Offer} offer */
  #offerUnits(offer) {
    const units = Number(offer.units);
    if (!Number.isFinite(units) || units < 1) return 0;
    return Math.floor(units);
  }

  /** @param {number} index */
  #isInitialTreatmentIndex(index) {
    if (Number.isInteger(this.#initialTreatmentIndex) && this.#initialTreatmentIndex >= 0) {
      return index === this.#initialTreatmentIndex;
    }
    const offer = this.#offers[index];
    return Boolean(offer && this.#offerUnits(offer) === 1);
  }

  /** @param {InitialSubscriptionOffer} sub */
  #getSubscriptionPricing(sub) {
    const allocations = sub.sellingPlanAllocations || [];
    if (allocations.length > 0) {
      const monthly = allocations.find((item) => {
        const label = `${item.optionName || item.name || ''}`.toLowerCase();
        return label.includes('mensal');
      });
      const allocation = monthly || allocations[0];
      return {
        price: allocation.price,
        compareAtPrice: allocation.compareAtPrice,
        sellingPlanId: allocation.sellingPlanId,
      };
    }

    let compareAt = sub.compareAtPrice;
    if (!compareAt || compareAt <= sub.price) {
      compareAt = null;
    }

    return {
      price: sub.price,
      compareAtPrice: compareAt,
      sellingPlanId: null,
    };
  }

  #hasInitialSubscription() {
    return Boolean(this.#initialSubscriptionOffer?.variantId);
  }

  #isInitialSubscriptionAvailable() {
    const sub = this.#initialSubscriptionOffer;
    if (!this.#hasInitialSubscription() || !sub) return false;
    return Boolean(sub.available);
  }

  /**
   * @param {Offer} offer
   * @param {'one_time'|'subscription'} mode
   * @param {number|null} sellingPlanId
   */
  #applyProtocolOffer(offer, mode, sellingPlanId) {
    const allocations = offer.sellingPlanAllocations || [];
    const canSubscribe = allocations.length > 0;
    let resolvedMode = mode;

    if (resolvedMode === 'subscription' && !canSubscribe) {
      resolvedMode = 'one_time';
    }

    let planId = null;
    if (resolvedMode === 'subscription') {
      const stillValid = allocations.find((item) => item.sellingPlanId === sellingPlanId);
      planId = stillValid?.sellingPlanId ?? allocations[0]?.sellingPlanId ?? null;
      if (!planId) resolvedMode = 'one_time';
    }

    const allocation =
      resolvedMode === 'subscription'
        ? allocations.find((item) => item.sellingPlanId === planId) || null
        : null;

    const price = allocation ? allocation.price : offer.price;
    const compareAtPrice = allocation
      ? allocation.compareAtPrice
      : offer.compareAtPrice && offer.compareAtPrice > offer.price
        ? offer.compareAtPrice
        : null;

    this.#state = {
      purchaseMode: resolvedMode,
      selectedOfferIndex: this.#offers.indexOf(offer),
      selectedProductId: offer.productId,
      selectedVariantId: offer.variantId,
      selectedSellingPlanId: resolvedMode === 'subscription' ? planId : null,
      selectedUnits: this.#offerUnits(offer),
      available: Boolean(offer.available),
      price,
      compareAtPrice,
    };
  }

  /**
   * @param {Offer} offer
   */
  #applyOffer(offer) {
    this.#initialInternalMode = 'one_time';
    this.#applyProtocolOffer(offer, 'one_time', null);
    if (this.#externalCheckout.enabled) {
      this.#state.available = Boolean(this.#getOfferCheckoutUrl(offer));
    }
  }

  /**
   * @param {Offer} offer
   * @returns {string}
   */
  #getOfferCheckoutUrl(offer) {
    const directUrl = typeof offer.checkoutUrl === 'string' ? offer.checkoutUrl.trim() : '';
    return directUrl;
  }

  /** @returns {string} */
  #getSelectedCheckoutUrl() {
    const offer = this.#offers[this.#state.selectedOfferIndex];
    return offer ? this.#getOfferCheckoutUrl(offer) : '';
  }

  /** @returns {string} */
  #getSubscriptionCheckoutUrl() {
    return this.#externalCheckout.subscriptionUrl;
  }

  /**
   * @param {string} url
   * @param {string} eventName
   */
  #redirectToCheckout(url, eventName = 'external_checkout_redirect') {
    const destination = String(url || '').trim();
    if (!destination) return false;

    this.#track(eventName);
    window.location.assign(destination);
    return true;
  }

  #onMainCheckout() {
    const url = this.#getSelectedCheckoutUrl();
    if (!url) return;

    const button = this.querySelector('[data-purency-purchase-cta]');
    if (button instanceof HTMLButtonElement) {
      button.classList.add('is-loading');
      button.disabled = true;
    }

    this.#redirectToCheckout(url, 'add_to_cart_submit');
  }

  #bindEvents() {
    const signal = this.#abortController?.signal;
    this.addEventListener('click', this.#onClick, { signal, capture: true });
    this.addEventListener('change', this.#onChange, { signal });

    if (!this.#externalCheckout.enabled) {
      const form = this.#form();
      if (form) {
        form.addEventListener('submit', this.#onFormSubmit, { signal });
      }
    }
  }

  #onMobileLayoutChange = (event) => {
    if (event.matches) {
      document.body.classList.add(PurencyProductPurchase.#PAGE_BODY_CLASS);
    } else {
      document.body.classList.remove(PurencyProductPurchase.#PAGE_BODY_CLASS);
    }
  };

  #applyPageLayout() {
    this.#mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    if (this.#mobileMediaQuery.matches) {
      document.body.classList.add(PurencyProductPurchase.#PAGE_BODY_CLASS);
    }
    this.#mobileMediaQuery.addEventListener('change', this.#onMobileLayoutChange);
  }

  #removePageLayout() {
    document.body.classList.remove(PurencyProductPurchase.#PAGE_BODY_CLASS);
    if (this.#mobileMediaQuery) {
      this.#mobileMediaQuery.removeEventListener('change', this.#onMobileLayoutChange);
      this.#mobileMediaQuery = null;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const subscriptionBtn = target.closest('[data-purency-purchase-subscription-cta]');
    if (subscriptionBtn instanceof HTMLElement && this.#externalCheckout.enabled) {
      const url = this.#getSubscriptionCheckoutUrl();
      if (!url) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#redirectToCheckout(url, 'subscription_checkout_redirect');
      return;
    }

    if (subscriptionBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#onSubscriptionAdd();
      return;
    }

    const mainCta = target.closest('[data-purency-purchase-cta]');
    if (mainCta instanceof HTMLElement && this.#externalCheckout.enabled) {
      const url = this.#getSelectedCheckoutUrl();
      if (!url) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#redirectToCheckout(url, 'add_to_cart_submit');
      return;
    }

    const compareToggle = target.closest('[data-purency-purchase-compare-toggle]');
    if (compareToggle instanceof HTMLElement) {
      this.#toggleCompare();
      return;
    }

    const stickerBtn = target.closest('[data-purency-purchase-sticker-btn]');
    if (stickerBtn instanceof HTMLElement) {
      this.#onStickerClick();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches('[data-purency-purchase-initial-mode-select]')) {
      const mode = target.value;
      if (mode === 'one_time' || mode === 'subscription') {
        this.#setInitialInternalMode(mode);
      }
      return;
    }

    if (!(target instanceof HTMLInputElement)) return;

    if (target.name === 'purency-purchase-offer') {
      const index = Number(target.value);
      const offer = this.#offers[index];
      if (!offer) return;

      this.#state.selectedOfferIndex = index;
      this.#applyOffer(offer);
      this.#track('offer_selected');
      this.#render();
      return;
    }

    if (target.name === 'purency-purchase-frequency') {
      const sellingPlanId = Number(target.value);
      const offer = this.#offers[this.#state.selectedOfferIndex];
      if (offer) this.#applyOffer(offer);
      this.#render();
    }
  };

  async #onSubscriptionAdd() {
    if (this.#externalCheckout.enabled) {
      const url = this.#getSubscriptionCheckoutUrl();
      if (!url) return;

      const button = this.querySelector('[data-purency-purchase-subscription-cta]');
      if (button instanceof HTMLButtonElement) {
        button.classList.add('is-loading');
        button.disabled = true;
      }

      this.#redirectToCheckout(url, 'subscription_checkout_redirect');
      return;
    }

    const sub = this.#initialSubscriptionOffer;
    if (!sub?.available) return;

    const pricing = this.#getSubscriptionPricing(sub);
    const button = this.querySelector('[data-purency-purchase-subscription-cta]');
    if (button instanceof HTMLButtonElement) {
      button.classList.add('is-loading');
      button.disabled = true;
    }

    try {
      const formData = new FormData();
      formData.set('id', String(sub.variantId));
      formData.set('quantity', '1');
      if (pricing.sellingPlanId) {
        formData.set('selling_plan', String(pricing.sellingPlanId));
      }

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [
            {
              merchandiseId: String(sub.variantId),
              quantity: 1,
            },
          ],
          promise: deferredEventPromise.promise,
        })
      );

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const payload = await response.json();

      if (payload.status) {
        console.error('[purency-product-purchase] Subscription add failed', payload);
        return;
      }

      this.#track('subscription_add_to_cart');
    } catch (error) {
      console.error('[purency-product-purchase] Subscription add failed', error);
    } finally {
      if (button instanceof HTMLButtonElement) {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }
  };

  /** @param {SubmitEvent} event */
  #onFormSubmit = (event) => {
    if (this.#externalCheckout.enabled) {
      event.preventDefault();
      event.stopPropagation();
      this.#onMainCheckout();
      return;
    }

    this.#track('add_to_cart_submit');
    const button = this.querySelector('[data-purency-purchase-cta]');
    if (button instanceof HTMLButtonElement) {
      button.classList.add('is-loading');
      window.setTimeout(() => button.classList.remove('is-loading'), 1800);
    }
  };

  /** @param {'one_time'|'subscription'} mode */
  #setInitialInternalMode(mode) {
    const index = this.#state.selectedOfferIndex;
    if (!this.#isInitialTreatmentIndex(index)) return;

    if (mode === 'subscription') {
      if (!this.#hasInitialSubscription()) return;
      if (!this.#isInitialSubscriptionAvailable()) return;
    }

    this.#initialInternalMode = mode;
    const offer = this.#offers[index];
    if (offer) this.#applyOffer(offer);
    this.#track('purchase_mode_selected');
    this.#render();
  }

  #toggleCompare() {
    const panel = this.querySelector('[data-purency-purchase-compare-panel]');
    const toggle = this.querySelector('[data-purency-purchase-compare-toggle]');
    if (!(panel instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

    const open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  #form() {
    return this.querySelector('form[data-type="add-to-cart-form"]');
  }

  /** @param {string} name */
  #track(name) {
    const offer = this.#offers[this.#state.selectedOfferIndex];
    const payload = {
      event: `purency_${name}`,
      page_type: 'product',
      product: 'serum-ghk-cu',
      offer_handle: offer?.productHandle || '',
      units: this.#state.selectedUnits,
      purchase_mode: this.#state.purchaseMode,
      price: this.#state.price / 100,
      currency: this.#currency,
      variant_id: this.#state.selectedVariantId,
    };

    window.dispatchEvent(new CustomEvent('purency:pdp-event', { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  }

  #syncFormInputs() {
    const form = this.#form();
    if (!(form instanceof HTMLFormElement)) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(this.#state.selectedVariantId);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (this.#state.purchaseMode === 'subscription' && this.#state.selectedSellingPlanId) {
      if (!(sellingPlanInput instanceof HTMLInputElement)) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        form.appendChild(sellingPlanInput);
      }
      sellingPlanInput.value = String(this.#state.selectedSellingPlanId);
      sellingPlanInput.disabled = false;
    } else if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.value = '';
      sellingPlanInput.disabled = true;
    }
  }

  /** @param {number} cents */
  #format(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  /**
   * @param {number} price
   * @param {number|null} compareAt
   */
  #savings(price, compareAt) {
    if (!compareAt || compareAt <= price) return null;
    const amount = compareAt - price;
    const percent = Math.round((amount / compareAt) * 100);
    return { amount, percent };
  }

  /**
   * @param {number} price
   * @param {number} units
   */
  #perUnit(price, units) {
    if (!units || units < 1) return null;
    return Math.round(price / units);
  }

  #ctaLabel() {
    if (!this.#state.available) return 'ESGOTADO';
    return 'COMPRAR';
  }

  #stickerActionLabel() {
    if (!this.#state.available) return 'ESGOTADO';
    return 'COMPRAR';
  }

  /**
   * @param {PurencyOffer} offer
   */
  #stickerOfferLabel(offer) {
    const units = this.#offerUnits(offer);
    if (units === 6) return 'Kit 6';
    if (units === 3) return 'Kit 3';
    if (units === 1) return '1 frasco';

    const label = this.#normalizeOfferLabel(offer.label || offer.title);
    return String(label).replace(/^Tratamento\s+/i, '');
  }

  #hasProtocolSellingPlans() {
    return this.#offers.some((offer) => offer.sellingPlanAllocations?.length > 0);
  }

  /** @param {{ offers?: boolean }} [options] */
  #render(options = {}) {
    this.#renderFrequencies();
    if (options.offers !== false) this.#renderOffers();
    this.#renderSubscriptionAside();
    this.#renderCompare();
    this.#renderCta();
    this.#renderStickyCta();
    this.#renderDisclaimer();
    this.#syncFormInputs();
    this.#syncPresentationState();
    this.#announce();
    this.#updateStickyVisibility();
  }

  #syncPresentationState() {
    this.classList.remove('is-initial-checkout', 'is-initial-subscription', 'is-initial-onetime');
    document.body.classList.remove(
      PurencyProductPurchase.#BODY_SUBSCRIPTION_CLASS,
      PurencyProductPurchase.#BODY_ONETIME_CLASS
    );
    this.#restoreProductMedia();
  }

  #clearPresentationState() {
    this.classList.remove('is-initial-checkout', 'is-initial-subscription', 'is-initial-onetime');
    document.body.classList.remove(
      PurencyProductPurchase.#BODY_SUBSCRIPTION_CLASS,
      PurencyProductPurchase.#BODY_ONETIME_CLASS
    );
    this.#restoreProductMedia();
  }

  #getPrimaryProductMediaImage() {
    const selectors = [
      '.product-information__media .product-media-container:first-child img',
      '.product-information__media slideshow-slide.product-media-container img',
      '.product-information__media .media-gallery__grid > .product-media-container:first-child img',
    ];

    for (const selector of selectors) {
      const img = document.querySelector(selector);
      if (img instanceof HTMLImageElement) return img;
    }

    return null;
  }

  #syncProductMedia() {
    const img = this.#getPrimaryProductMediaImage();
    if (!img) return;

    if (!this.#productMediaOriginal) {
      this.#productMediaOriginal = {
        src: img.currentSrc || img.src,
        srcset: img.srcset,
      };
    }

    const subscriptionImage = this.#initialSubscriptionVisualImage;
    const useSubscription =
      this.classList.contains('is-initial-subscription') && Boolean(subscriptionImage);

    if (useSubscription) {
      if (img.src !== subscriptionImage) img.src = subscriptionImage;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      return;
    }

    this.#restoreProductMedia();
  }

  #restoreProductMedia() {
    const img = this.#getPrimaryProductMediaImage();
    if (!img || !this.#productMediaOriginal) return;

    if (img.src !== this.#productMediaOriginal.src) {
      img.src = this.#productMediaOriginal.src;
    }

    if (this.#productMediaOriginal.srcset) {
      img.srcset = this.#productMediaOriginal.srcset;
    } else {
      img.removeAttribute('srcset');
    }
  }

  #onStickerClick() {
    if (this.#externalCheckout.enabled) {
      const url = this.#getSelectedCheckoutUrl();
      if (url) {
        this.#track('sticky_cta_clicked');
        this.#redirectToCheckout(url, 'sticky_checkout_redirect');
        return;
      }
    }

    this.#track('sticky_cta_clicked');
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const mainCta = this.querySelector('[data-purency-purchase-cta]');
    window.setTimeout(() => {
      if (mainCta instanceof HTMLButtonElement && !mainCta.disabled) {
        mainCta.focus({ preventScroll: true });
      }
    }, 420);
  }

  #setupStickyCta() {
    const sticker = this.querySelector('[data-purency-purchase-sticker]');
    if (!(sticker instanceof HTMLElement)) return;

    const resultsSection =
      document.getElementById('purency-results-real') || document.querySelector('.purency-results-real');

    if (!resultsSection) {
      sticker.hidden = true;
      return;
    }

    this.#stickyScrollHandler = () => {
      this.#updateStickyVisibility(resultsSection, sticker);
    };

    const signal = this.#abortController?.signal;
    window.addEventListener('scroll', this.#stickyScrollHandler, { passive: true, signal });
    window.addEventListener('resize', this.#stickyScrollHandler, { passive: true, signal });
    this.#stickyScrollHandler();
  }

  /**
   * @param {Element} resultsSection
   * @param {HTMLElement} sticker
   */
  #updateStickyVisibility(resultsSection, sticker) {
    if (!resultsSection || !sticker) {
      const fallbackSticker = this.querySelector('[data-purency-purchase-sticker]');
      const fallbackResults =
        document.getElementById('purency-results-real') || document.querySelector('.purency-results-real');
      if (!(fallbackSticker instanceof HTMLElement) || !fallbackResults) return;
      resultsSection = fallbackResults;
      sticker = fallbackSticker;
    }

    const purchaseBottom = this.getBoundingClientRect().bottom;
    const resultsTop = resultsSection.getBoundingClientRect().top;
    let show = purchaseBottom < 96 && resultsTop < window.innerHeight * 0.92;

    const viralSection = document.querySelector('.purency-viral-videos');
    if (show && viralSection instanceof HTMLElement) {
      const viralRect = viralSection.getBoundingClientRect();
      const stickerZoneTop = window.innerHeight - 76;
      const viralOverlapsSticker =
        viralRect.top < window.innerHeight * 0.88 && viralRect.bottom > stickerZoneTop;
      if (viralOverlapsSticker) {
        show = false;
      }
    }

    sticker.hidden = false;
    sticker.classList.toggle('is-visible', show);
    document.body.classList.toggle(PurencyProductPurchase.#STICKER_BODY_CLASS, show);
  }

  #renderStickyCta() {
    const summaryEl = this.querySelector('[data-purency-purchase-sticker-summary]');
    const actionEl = this.querySelector('[data-purency-purchase-sticker-action]');
    if (!(summaryEl instanceof HTMLElement) || !(actionEl instanceof HTMLElement)) {
      return;
    }

    const offer = this.#offers[this.#state.selectedOfferIndex];
    if (!offer) return;

    const kitLabel = this.#stickerOfferLabel(offer);
    const price = this.#format(this.#state.price);
    summaryEl.textContent = `${kitLabel} · ${price}`;
    actionEl.textContent = this.#stickerActionLabel();
    const stickerBtn = this.querySelector('[data-purency-purchase-sticker-btn]');
    if (stickerBtn instanceof HTMLButtonElement) {
      stickerBtn.disabled = !this.#state.available;
    }
  }

  /** @returns {number} */
  #getInitialTreatmentIndex() {
    if (
      Number.isInteger(this.#initialTreatmentIndex) &&
      this.#initialTreatmentIndex >= 0 &&
      this.#initialTreatmentIndex < this.#offers.length
    ) {
      return this.#initialTreatmentIndex;
    }

    return this.#offers.findIndex((offer, index) => this.#isInitialTreatmentIndex(index));
  }

  #renderFrequencies() {
    const root = this.querySelector('[data-purency-purchase-frequencies]');
    if (!(root instanceof HTMLElement)) return;

    const offer = this.#offers[this.#state.selectedOfferIndex];
    const isInitialSubscription =
      this.#isInitialTreatmentIndex(this.#state.selectedOfferIndex) &&
      this.#initialInternalMode === 'subscription' &&
      this.#hasInitialSubscription();

    const show =
      !isInitialSubscription &&
      this.#state.purchaseMode === 'subscription' &&
      Boolean(offer?.sellingPlanAllocations?.length);

    root.hidden = !show;
    if (!show || !offer) {
      root.innerHTML = '';
      return;
    }

    const options = offer.sellingPlanAllocations
      .map((allocation) => {
        const checked = allocation.sellingPlanId === this.#state.selectedSellingPlanId;
        const label = allocation.optionName || allocation.name;
        return `
          <label class="purency-purchase__freq-option">
            <input
              type="radio"
              name="purency-purchase-frequency"
              value="${allocation.sellingPlanId}"
              ${checked ? 'checked' : ''}
            >
            <span>${this.#escape(label)}</span>
          </label>
        `;
      })
      .join('');

    root.innerHTML = `
      <fieldset class="purency-purchase__frequencies">
        <legend>Frequência</legend>
        <div class="purency-purchase__freq-list">${options}</div>
      </fieldset>
    `;
  }

  /**
   * @param {Offer} offer
   * @param {{ subscriptionIcon?: boolean }} [options]
   */
  #getOfferVisualImage(offer, options = {}) {
    if (options.subscriptionIcon) {
      return (
        this.#initialSubscriptionVisualImage ||
        offer.visualImage ||
        offer.featuredImage ||
        this.#initialSubscriptionOffer?.featuredImage ||
        null
      );
    }

    return (
      offer.visualImage ||
      offer.featuredImage ||
      this.#initialSubscriptionOffer?.featuredImage ||
      null
    );
  }

  /**
   * @param {Offer} offer
   * @param {{ subscriptionIcon?: boolean }} [options]
   */
  #renderProductVisual(offer, options = {}) {
    const subscriptionIcon = options.subscriptionIcon === true;
    const useKit = options.useKit === true;
    const productImage = this.#getOfferVisualImage(offer, { subscriptionIcon });
    if (!productImage) return '';

    const imageAlt =
      offer.title ||
      offer.productTitle ||
      this.#initialSubscriptionOffer?.productTitle ||
      'Sérum GHK-Cu';

    const visualWidth = subscriptionIcon ? 128 : useKit ? 220 : 140;
    const visualHeight = subscriptionIcon ? 170 : useKit ? 176 : 105;
    const visualClass = useKit ? 'purency-purchase__kit-visual' : 'purency-purchase__offer-visual';
    const wrapClass = useKit ? 'purency-purchase__kit-visual-frame' : 'purency-purchase__offer-visual-wrap';
    const imageClass = useKit ? 'purency-purchase__kit-visual-image' : 'purency-purchase__offer-visual-image';

    const iconMarkup = subscriptionIcon
      ? `
        <span class="purency-purchase__offer-visual-icon" aria-label="Assinatura mensal">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      `
      : '';

    return `
      <div class="${visualClass}">
        <div class="${wrapClass}">
          <img
            class="${imageClass}"
            src="${this.#escape(productImage)}"
            alt="${this.#escape(imageAlt)}"
            width="${visualWidth}"
            height="${visualHeight}"
            loading="lazy"
            decoding="async"
          >
        </div>
        ${iconMarkup}
      </div>
    `;
  }

  /**
   * @param {Offer} offer
   * @param {boolean} selected
   */
  #renderInitialModeControl(offer, selected) {
    if (!selected || !this.#hasInitialSubscription()) return '';

    const subscriptionSelected = this.#initialInternalMode === 'subscription';
    const subscriptionAvailable = this.#isInitialSubscriptionAvailable();
    const sub = this.#initialSubscriptionOffer;
    const pricing = sub ? this.#getSubscriptionPricing(sub) : null;

    const modeControl = `
      <div
        class="purency-purchase__initial-mode"
        role="group"
        aria-label="Modalidade do Tratamento Inicial"
      >
        <button
          type="button"
          class="purency-purchase__initial-mode-btn"
          data-purency-purchase-initial-mode="subscription"
          aria-pressed="${subscriptionSelected ? 'true' : 'false'}"
          ${subscriptionAvailable ? '' : 'disabled'}
        >
          ASSINE TODO MÊS
        </button>
        <button
          type="button"
          class="purency-purchase__initial-mode-btn"
          data-purency-purchase-initial-mode="one_time"
          aria-pressed="${subscriptionSelected ? 'false' : 'true'}"
        >
          COMPRA ÚNICA
        </button>
      </div>
    `;

    if (subscriptionSelected && subscriptionAvailable && pricing) {
      const savings = this.#savings(pricing.price, offer.price);
      const savingsText = savings
        ? `−${this.#escape(this.#format(savings.amount))}`
        : '';

      return `
        ${modeControl}
        ${
          savingsText
            ? `<div class="purency-purchase__decision-subscription"><p>${savingsText} / entrega</p></div>`
            : ''
        }
      `;
    }

    return modeControl;
  }

  /**
   * @param {Offer} offer
   * @param {boolean} selected
   */
  #renderStandardExpanded(offer, selected) {
    if (!selected) return '';

    const units = this.#offerUnits(offer);
    const kitLabel = this.#kitItemLabel;
    const price = this.#state.price;
    const compareAt = this.#state.compareAtPrice;
    const savings = this.#savings(price, compareAt);
    const perUnit = this.#perUnit(price, units);
    const shipping = (offer.shippingLabel || '').trim();
    const visual = this.#renderProductVisual(offer, { useKit: true });
    const hasVisual = Boolean(visual);

    const kitItemText =
      units > 0 && kitLabel
        ? `${units}× ${this.#escape(kitLabel)}`
        : units > 0
          ? `${units} unidade${units > 1 ? 's' : ''}`
          : '';

    const kitBlock =
      kitItemText && !hasVisual
        ? `<div class="purency-purchase__offer-kit">
            <p class="purency-purchase__offer-kit-title">O QUE VOCÊ RECEBE</p>
            <p class="purency-purchase__offer-kit-item">${kitItemText}</p>
          </div>`
        : '';

    const kitInline =
      kitItemText && hasVisual
        ? `<p class="purency-purchase__kit-quantity">${kitItemText}</p>`
        : '';

    const statsBlock = `
      <div class="purency-purchase__kit-metrics-grid">
        ${
          units > 0
            ? `
              <div class="purency-purchase__kit-metric">
                <span class="purency-purchase__kit-metric-label">Unidades</span>
                <span class="purency-purchase__kit-metric-value">${units}</span>
              </div>
            `
            : ''
        }
        ${
          perUnit
            ? `
              <div class="purency-purchase__kit-metric">
                <span class="purency-purchase__kit-metric-label">Por frasco</span>
                <span class="purency-purchase__kit-metric-value">${this.#escape(this.#format(perUnit))}</span>
              </div>
            `
            : ''
        }
        ${
          savings
            ? `
              <div class="purency-purchase__kit-metric is-highlight">
                <span class="purency-purchase__kit-metric-label">Economia</span>
                <span class="purency-purchase__kit-metric-value">${this.#escape(this.#format(savings.amount))}</span>
              </div>
            `
            : ''
        }
      </div>
    `;

    const notesBlock = `
      ${
        shipping
          ? `<p class="purency-purchase__kit-meta">${this.#escape(shipping)}</p>`
          : ''
      }
      ${
        offer.duration
          ? `<p class="purency-purchase__offer-note">Duração estimada: ${this.#escape(offer.duration)}</p>`
          : ''
      }
      ${
        offer.description
          ? `<p class="purency-purchase__offer-note">${this.#escape(offer.description)}</p>`
          : ''
      }
      ${offer.available ? '' : '<p class="purency-purchase__kit-meta">Esgotado</p>'}
    `;

    const detailBlock =
      hasVisual
        ? `
          <div class="purency-purchase__kit-stage">
            ${visual}
            <div class="purency-purchase__kit-metrics">
              ${kitInline}
              ${statsBlock}
            </div>
          </div>
        `
        : `${kitBlock}${statsBlock}`;

    return `${detailBlock}${notesBlock}`;
  }

  /**
   * @param {Offer} offer
   * @param {number} index
   */
  #renderKitItem(offer, index) {
    const selected = index === this.#state.selectedOfferIndex;
    const configuredFeaturedIndex = Number(this.dataset.featuredOfferPosition || '2') - 1;
    const isFeaturedOffer = offer.featured || index === configuredFeaturedIndex;
    const isInitialTreatment = this.#isInitialTreatmentIndex(index);
    const initialSubscriptionActive =
      selected &&
      isInitialTreatment &&
      this.#initialInternalMode === 'subscription' &&
      this.#hasInitialSubscription() &&
      this.#isInitialSubscriptionAvailable();
    const showSubscriptionUi = this.#hasProtocolSellingPlans();

    const allocation =
      selected && this.#state.purchaseMode === 'subscription' && !initialSubscriptionActive
        ? offer.sellingPlanAllocations.find(
            (item) => item.sellingPlanId === this.#state.selectedSellingPlanId
          ) || null
        : !selected &&
            showSubscriptionUi &&
            this.#state.purchaseMode === 'subscription' &&
            !isInitialTreatment &&
            (offer.sellingPlanAllocations?.length ?? 0) > 0
          ? offer.sellingPlanAllocations.find(
              (item) => item.sellingPlanId === this.#state.selectedSellingPlanId
            ) || offer.sellingPlanAllocations[0]
          : null;

    let price = selected ? this.#state.price : allocation ? allocation.price : offer.price;
    let compareAt = selected
      ? this.#state.compareAtPrice
      : allocation
        ? allocation.compareAtPrice
        : offer.compareAtPrice && offer.compareAtPrice > offer.price
          ? offer.compareAtPrice
          : null;

    if (!selected && isInitialTreatment && this.#hasInitialSubscription()) {
      price = offer.price;
      compareAt =
        offer.compareAtPrice && offer.compareAtPrice > offer.price ? offer.compareAtPrice : null;
    }

    const savings = this.#savings(price, compareAt);
    const units = this.#offerUnits(offer);
    const secondaryLine =
      offer.subtitle || (units > 0 ? `${units} unidade${units > 1 ? 's' : ''}` : '');
    const hasSubscription = (offer.sellingPlanAllocations?.length ?? 0) > 0;
    const modeLabel =
      showSubscriptionUi && this.#state.purchaseMode === 'subscription' && selected && hasSubscription
        ? 'Assinatura'
        : '';
    const frequencyLabel =
      showSubscriptionUi && selected && allocation ? allocation.optionName || allocation.name : '';
    const oneTimeOnlyNote =
      showSubscriptionUi &&
      this.#state.purchaseMode === 'subscription' &&
      selected &&
      !hasSubscription
        ? 'Disponível somente em compra única'
        : '';
    const unavailableNote = offer.available ? '' : 'Indisponível';
    const indexLabel = String(index + 1).padStart(2, '0');
    const showPriceCol = !initialSubscriptionActive;

    const priceCol = showPriceCol
      ? `
        <div class="purency-purchase__kit-price-col">
          ${
            selected && compareAt
              ? `<span class="purency-purchase__kit-compare">${this.#escape(this.#format(compareAt))}</span>`
              : ''
          }
          <span class="purency-purchase__kit-price">${this.#escape(this.#format(price))}</span>
          ${
            selected && savings
              ? `<span class="purency-purchase__kit-save">${savings.percent}%</span>`
              : ''
          }
        </div>
      `
      : '';

    let bodyHtml = '';
    if (selected) {
      const badgeHtml = offer.badge
        ? `<span class="purency-purchase__kit-badge">${this.#escape(offer.badge)}</span>`
        : '';

      if (isInitialTreatment && this.#hasInitialSubscription()) {
        bodyHtml = `
          <div class="purency-purchase__kit-body" data-purency-kit-panel>
            ${badgeHtml}
            ${this.#renderStandardExpanded(offer, true)}
          </div>
        `;
      } else {
        bodyHtml = `
          <div class="purency-purchase__kit-body" data-purency-kit-panel>
            ${badgeHtml}
            ${this.#renderStandardExpanded(offer, true)}
            ${
              modeLabel && !isInitialTreatment
                ? `<p class="purency-purchase__kit-meta">${this.#escape(modeLabel)}</p>`
                : ''
            }
            ${
              frequencyLabel && !isInitialTreatment
                ? `<p class="purency-purchase__kit-meta">${this.#escape(frequencyLabel)}</p>`
                : ''
            }
            ${
              oneTimeOnlyNote && !isInitialTreatment
                ? `<p class="purency-purchase__kit-meta">${this.#escape(oneTimeOnlyNote)}</p>`
                : ''
            }
          </div>
        `;
      }
    }

    return `
      <div
        class="purency-purchase__kit ${selected ? 'is-selected' : ''} ${
          isFeaturedOffer ? 'is-featured' : ''
        } ${initialSubscriptionActive ? 'is-initial-sub-active' : ''} ${offer.available ? '' : 'is-unavailable'}"
      >
        <input
          class="purency-purchase__offer-input"
          type="radio"
          name="purency-purchase-offer"
          id="purency-purchase-offer-${this.dataset.blockId}-${index}"
          value="${index}"
          ${selected ? 'checked' : ''}
          ${offer.available ? '' : 'disabled'}
        >
        <label
          class="purency-purchase__kit-trigger"
          for="purency-purchase-offer-${this.dataset.blockId}-${index}"
        >
          <span class="purency-purchase__kit-index">${indexLabel}</span>
          <div class="purency-purchase__kit-summary">
            <div class="purency-purchase__kit-title-row">
              <span class="purency-purchase__kit-name">${this.#escape(
                this.#normalizeOfferLabel(offer.label || offer.title)
              )}</span>
              ${
                isFeaturedOffer
                  ? '<span class="purency-purchase__kit-tag">Recomendado</span>'
                  : ''
              }
            </div>
            ${
              secondaryLine && selected
                ? `<p class="purency-purchase__kit-desc">${this.#escape(secondaryLine)}</p>`
                : ''
            }
            ${
              unavailableNote && !selected
                ? `<p class="purency-purchase__kit-status">${this.#escape(unavailableNote)}</p>`
                : ''
            }
          </div>
          ${priceCol}
        </label>
        ${bodyHtml}
      </div>
    `;
  }

  /**
   * @param {Offer} offer
   * @param {number} index
   */
  #protocolPricing(offer, index) {
    if (index === this.#state.selectedOfferIndex) {
      return {
        price: this.#state.price,
        compareAt: this.#state.compareAtPrice,
      };
    }

    return {
      price: offer.price,
      compareAt:
        offer.compareAtPrice && offer.compareAtPrice > offer.price ? offer.compareAtPrice : null,
    };
  }

  #renderProtocolStage() {
    const index = this.#state.selectedOfferIndex;
    const offer = this.#offers[index];
    if (!offer) return '';

    const configuredFeaturedIndex = Number(this.dataset.featuredOfferPosition || '2') - 1;
    const isFeatured = offer.featured || index === configuredFeaturedIndex;
    const isInitialTreatment = this.#isInitialTreatmentIndex(index);
    const subscriptionActive =
      isInitialTreatment &&
      this.#initialInternalMode === 'subscription' &&
      this.#hasInitialSubscription() &&
      this.#isInitialSubscriptionAvailable();
    const { price, compareAt } = this.#protocolPricing(offer, index);
    const savings = this.#savings(price, compareAt);
    const units = this.#offerUnits(offer);
    const perUnit = this.#perUnit(price, units);
    const title = this.#normalizeOfferLabel(offer.label || offer.title);
    const duration = (offer.duration || '').trim() || `${units} frasco${units === 1 ? '' : 's'}`;
    const indexLabel = String(index + 1).padStart(2, '0');
    const badge = isFeatured
      ? '<span class="purency-purchase__observatory-recommended">Recomendado</span>'
      : offer.badge
        ? `<span class="purency-purchase__observatory-badge">${this.#escape(offer.badge)}</span>`
        : '';
    const mode = isInitialTreatment ? this.#renderInitialModeControl(offer, true) : '';
    const supportingCopy = [offer.shippingLabel, offer.description].filter(Boolean).join(' · ');

    return `
      <section
        class="purency-purchase__observatory-stage"
        data-purency-observatory-stage
        data-protocol-index="${index}"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="purency-purchase__observatory-viewport purency-purchase__observatory-viewport--data">
          <div class="purency-purchase__observatory-hud" aria-hidden="true">
            <span>PUR-${indexLabel}</span>
            <span>GHK-Cu / 30 ML</span>
          </div>
          <div class="purency-purchase__observatory-signal" aria-hidden="true">
            <span class="purency-purchase__observatory-orbit purency-purchase__observatory-orbit--outer"></span>
            <span class="purency-purchase__observatory-orbit purency-purchase__observatory-orbit--inner"></span>
            <span class="purency-purchase__observatory-core"></span>
          </div>
          <div class="purency-purchase__observatory-signature" aria-hidden="true">
            <span>${units}×</span>
            <strong>${indexLabel}</strong>
            <small>PROTOCOLO / GHK-Cu</small>
          </div>
          <span class="purency-purchase__observatory-scan" aria-hidden="true"></span>
        </div>

        <div class="purency-purchase__observatory-dossier">
          <div class="purency-purchase__observatory-copy">
            <div class="purency-purchase__observatory-kicker">
              <span>Protocolo ${indexLabel}</span>
              ${badge}
            </div>
            <h3 class="purency-purchase__observatory-title">${this.#escape(title)}</h3>
            ${
              offer.subtitle
                ? `<p class="purency-purchase__observatory-subtitle">${this.#escape(offer.subtitle)}</p>`
                : ''
            }
          </div>

          <div class="purency-purchase__observatory-price">
            ${
              compareAt
                ? `<span class="purency-purchase__observatory-compare">${this.#escape(this.#format(compareAt))}</span>`
                : ''
            }
            <strong>${this.#escape(this.#format(price))}</strong>
            ${
              savings
                ? `<span>${savings.percent}% de economia</span>`
                : subscriptionActive
                  ? '<span>cobrança mensal</span>'
                  : ''
            }
          </div>
        </div>

        <div class="purency-purchase__observatory-metrics" aria-label="Detalhes do protocolo">
          <div class="purency-purchase__observatory-metric">
            <span>Continuidade</span>
            <strong>${this.#escape(duration)}</strong>
          </div>
          <div class="purency-purchase__observatory-metric">
            <span>Por frasco</span>
            <strong>${perUnit ? this.#escape(this.#format(perUnit)) : '—'}</strong>
          </div>
          <div class="purency-purchase__observatory-metric ${savings ? 'is-highlight' : ''}">
            <span>Economia</span>
            <strong>${savings ? this.#escape(this.#format(savings.amount)) : '—'}</strong>
          </div>
        </div>

        ${mode}
        ${
          supportingCopy
            ? `<p class="purency-purchase__observatory-note">${this.#escape(supportingCopy)}</p>`
            : ''
        }
      </section>
    `;
  }

  #renderProtocolSelector() {
    const configuredFeaturedIndex = Number(this.dataset.featuredOfferPosition || '2') - 1;

    return this.#offers
      .map((offer, index) => {
        const selected = index === this.#state.selectedOfferIndex;
        const isFeatured = offer.featured || index === configuredFeaturedIndex;
        const units = this.#offerUnits(offer);
        const title = this.#normalizeOfferLabel(offer.label || offer.title);
        const duration =
          (offer.duration || '').trim() || `${units} frasco${units === 1 ? '' : 's'}`;
        const { price } = this.#protocolPricing(offer, index);
        const indexLabel = String(index + 1).padStart(2, '0');
        const suffix =
          selected &&
          this.#isInitialTreatmentIndex(index) &&
          this.#initialInternalMode === 'subscription'
            ? '/mês'
            : '';

        return `
          <div class="purency-purchase__protocol-option ${selected ? 'is-selected' : ''} ${
            isFeatured ? 'is-featured' : ''
          } ${offer.available ? '' : 'is-unavailable'}">
            <input
              class="purency-purchase__offer-input"
              type="radio"
              name="purency-purchase-offer"
              id="purency-purchase-offer-${this.dataset.blockId}-${index}"
              value="${index}"
              ${selected ? 'checked' : ''}
              ${offer.available ? '' : 'disabled'}
            >
            <label
              class="purency-purchase__protocol-option-label"
              for="purency-purchase-offer-${this.dataset.blockId}-${index}"
            >
              <span class="purency-purchase__protocol-option-index">${indexLabel}</span>
              <span class="purency-purchase__protocol-option-copy">
                <span class="purency-purchase__protocol-option-name">${this.#escape(title)}</span>
                <span class="purency-purchase__protocol-option-meta">
                  ${this.#escape(duration)}${isFeatured ? ' · escolha Purency' : ''}
                </span>
              </span>
              <span class="purency-purchase__protocol-option-price">
                ${this.#escape(this.#format(price))}${suffix}
              </span>
              <span class="purency-purchase__protocol-option-mark" aria-hidden="true"></span>
            </label>
          </div>
        `;
      })
      .join('');
  }

  #sortedOfferIndices() {
    const displayOrder = [6, 3, 1];

    return this.#offers
      .map((offer, index) => ({ index, units: this.#offerUnits(offer) }))
      .sort((a, b) => {
        const rankA = displayOrder.indexOf(a.units);
        const rankB = displayOrder.indexOf(b.units);
        const orderA = rankA >= 0 ? rankA : 99;
        const orderB = rankB >= 0 ? rankB : 99;
        return orderA - orderB;
      })
      .map((item) => item.index);
  }

  /**
   * @param {Offer} offer
   */
  #pulseRowPricing(offer) {
    const price = offer.price;
    const compareAt =
      offer.compareAtPrice && offer.compareAtPrice > price ? offer.compareAtPrice : null;

    return { price, compareAt };
  }

  /**
   * @param {Offer} offer
   * @param {number} units
   */
  #renderPulseBadge(units) {
    if (units === 6) {
      return `<span class="purency-purchase__pulse-badge">Melhor custo-benefício</span>`;
    }
    if (units === 3) {
      return `<span class="purency-purchase__pulse-badge">Mais escolhido</span>`;
    }
    return '';
  }

  /**
   * @param {number} units
   * @param {number|null} perUnit
   */
  #treatmentDetail(units, perUnit) {
    if (units === 1) return '1 frasco';
    if (perUnit) return `${units} frascos - ${this.#format(perUnit)} / frasco`;
    return `${units} frascos`;
  }

  /**
   * @param {number} units
   */
  #treatmentLabel(units) {
    if (units === 6) return 'Tratamento Completo';
    if (units === 3) return 'Tratamento Recomendado';
    if (units === 1) return 'Tratamento Inicial';
    return `${units} frascos`;
  }

  #getInitialTreatmentOffer() {
    const index = this.#getInitialTreatmentIndex();
    if (index < 0) return null;
    return this.#offers[index] ?? null;
  }

  #shouldShowSubscriptionAside() {
    if (this.#externalCheckout.enabled) {
      return Boolean(this.#getSubscriptionCheckoutUrl());
    }
    return this.#hasInitialSubscription() && this.#isInitialSubscriptionAvailable();
  }

  #renderSubscriptionAside() {
    const root = this.querySelector('[data-purency-purchase-subscription]');
    const disclaimer = this.querySelector('[data-purency-purchase-subscription-disclaimer]');
    const show = this.#shouldShowSubscriptionAside();

    if (disclaimer instanceof HTMLElement) {
      disclaimer.hidden = !show;
    }

    if (!(root instanceof HTMLElement)) return;

    if (!show) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }

    const sub = this.#initialSubscriptionOffer;
    if (!sub) return;

    const pricing = this.#getSubscriptionPricing(sub);
    const initialOffer = this.#getInitialTreatmentOffer();
    const oneTimePrice = initialOffer?.price ?? pricing.price;
    const compareAt =
      oneTimePrice > pricing.price
        ? oneTimePrice
        : pricing.compareAtPrice && pricing.compareAtPrice > pricing.price
          ? pricing.compareAtPrice
          : null;
    const subPriceLabel = `${this.#format(pricing.price)}/mês`;
    const ctaLabel = 'ASSINAR E ECONOMIZAR';
    const visualImage = this.#initialSubscriptionVisualImage || sub.featuredImage || '';
    const visualMarkup = visualImage
      ? `<span class="purency-purchase__subscription-visual" aria-hidden="true">
          <img
            class="purency-purchase__subscription-visual-image"
            src="${this.#escape(visualImage)}"
            alt=""
            width="52"
            height="52"
            loading="lazy"
            decoding="async"
          >
        </span>`
      : '';
    const compareMarkup =
      compareAt && compareAt > pricing.price
        ? `<span class="purency-purchase__subscription-compare">de ${this.#escape(this.#format(compareAt))}</span>`
        : '';
    const subscriptionUrl = this.#getSubscriptionCheckoutUrl();
    const subscriptionCtaMarkup = this.#externalCheckout.enabled
      ? `<a
          href="${this.#escape(subscriptionUrl)}"
          class="purency-purchase__subscription-cta"
          data-purency-purchase-subscription-cta
          rel="noopener"
        >
          <span class="purency-purchase__subscription-cta-text">${this.#escape(ctaLabel)}</span>
          <span class="purency-purchase__subscription-cta-arrow" aria-hidden="true">→</span>
        </a>`
      : `<button
          type="button"
          class="purency-purchase__subscription-cta"
          data-purency-purchase-subscription-cta
        >
          <span class="purency-purchase__subscription-cta-text">${this.#escape(ctaLabel)}</span>
          <span class="purency-purchase__subscription-cta-arrow" aria-hidden="true">→</span>
        </button>`;

    root.hidden = false;
    root.innerHTML = `
      <div class="purency-purchase__subscription-card">
        <span class="purency-purchase__pulse-badge purency-purchase__subscription-badge">Melhor opção</span>
        <div class="purency-purchase__subscription-main">
          ${visualMarkup}
          <div class="purency-purchase__subscription-copy">
            <p class="purency-purchase__subscription-eyebrow">Assinatura mensal</p>
            <h3 class="purency-purchase__subscription-title">Receba todo mês com melhor preço</h3>
            <p class="purency-purchase__subscription-price">
              <strong>${this.#escape(subPriceLabel)}</strong>
              ${compareMarkup}
            </p>
          </div>
        </div>
        ${subscriptionCtaMarkup}
      </div>
    `;
  }

  /**
   * @param {Offer} offer
   * @param {number} units
   */
  #renderPulseVisual(offer, units) {
    const image = offer.visualImage || offer.featuredImage || null;
    if (!image) return '';

    const alt = this.#treatmentLabel(units);

    return `
      <span class="purency-purchase__pulse-visual" aria-hidden="true">
        <img
          class="purency-purchase__pulse-visual-image"
          src="${this.#escape(image)}"
          alt=""
          width="46"
          height="46"
          loading="lazy"
          decoding="async"
        >
      </span>
    `;
  }

  #renderPulseItems() {
    const configuredFeaturedIndex = Number(this.dataset.featuredOfferPosition || '2') - 1;

    return this.#sortedOfferIndices()
      .map((index) => {
        const offer = this.#offers[index];
        const selected = index === this.#state.selectedOfferIndex;
        const isFeatured = offer.featured || index === configuredFeaturedIndex;
        const units = this.#offerUnits(offer);
        const treatmentLabel = this.#treatmentLabel(units);
        let { price, compareAt } = this.#pulseRowPricing(offer);
        const perUnit = units === 3 || units === 6 ? this.#perUnit(price, units) : null;
        const isInitial = this.#isInitialTreatmentIndex(index);
        const badgeMarkup = this.#renderPulseBadge(units);
        let treatmentDetail = this.#treatmentDetail(units, perUnit);

        if (isInitial && selected) {
          price = this.#state.price;
          compareAt =
            this.#state.compareAtPrice && this.#state.compareAtPrice > price
              ? this.#state.compareAtPrice
              : compareAt;
          treatmentDetail = '1 frasco';
        }
        const compareHtml =
          compareAt
            ? `<span class="purency-purchase__pulse-compare">de ${this.#escape(this.#format(compareAt))}</span>`
            : '';
        const visualMarkup = this.#renderPulseVisual(offer, units);

        return `
          <div
            class="purency-purchase__pulse-item ${selected ? 'is-selected' : ''} ${
              isFeatured ? 'is-featured' : ''
            } ${isInitial ? 'is-initial' : ''} ${offer.available ? '' : 'is-unavailable'}"
            data-pulse-index="${index}"
          >
            ${badgeMarkup}
            <input
              class="purency-purchase__offer-input"
              type="radio"
              name="purency-purchase-offer"
              id="purency-purchase-offer-${this.dataset.blockId}-${index}"
              value="${index}"
              aria-label="${this.#escape(treatmentLabel)}"
              ${selected ? 'checked' : ''}
              ${offer.available ? '' : 'disabled'}
            >
            <label
              class="purency-purchase__pulse-label"
              for="purency-purchase-offer-${this.dataset.blockId}-${index}"
            >
              <span class="purency-purchase__pulse-mark" aria-hidden="true"></span>
              ${visualMarkup}
              <span class="purency-purchase__cycle-body">
                <span class="purency-purchase__cycle-row purency-purchase__cycle-row--top">
                  <span class="purency-purchase__cycle-name">${this.#escape(treatmentLabel)}</span>
                  ${compareHtml}
                </span>
                <span class="purency-purchase__cycle-row purency-purchase__cycle-row--bottom">
                  <span class="purency-purchase__cycle-detail">${this.#escape(treatmentDetail)}</span>
                  <strong class="purency-purchase__pulse-total">${this.#escape(this.#format(price))}</strong>
                </span>
              </span>
            </label>
          </div>
        `;
      })
      .join('');
  }

  #renderOffers() {
    const root = this.querySelector('[data-purency-purchase-offers]');
    if (!(root instanceof HTMLElement)) return;

    root.innerHTML = `
      <div class="purency-purchase__motor">
        <fieldset
          class="purency-purchase__pulse"
          data-purency-pulse
          data-selected-index="${this.#state.selectedOfferIndex}"
        >
          <legend class="visually-hidden">Quantidade</legend>
          <div class="purency-purchase__pulse-list">
            ${this.#renderPulseItems()}
          </div>
        </fieldset>
      </div>
    `;
  }

  #renderCompare() {
    const body = this.querySelector('[data-purency-purchase-compare-body]');
    if (!(body instanceof HTMLElement)) return;

    const rows = this.#offers
      .map((offer) => {
        const allocation =
          this.#state.purchaseMode === 'subscription'
            ? offer.sellingPlanAllocations.find(
                (item) => item.sellingPlanId === this.#state.selectedSellingPlanId
              ) || offer.sellingPlanAllocations[0]
            : null;
        const price =
          this.#state.purchaseMode === 'subscription' && allocation ? allocation.price : offer.price;
        const compareAt =
          this.#state.purchaseMode === 'subscription' && allocation
            ? allocation.compareAtPrice
            : offer.compareAtPrice && offer.compareAtPrice > offer.price
              ? offer.compareAtPrice
              : null;
        const savings = this.#savings(price, compareAt);
        const units = this.#offerUnits(offer);
        const perUnit = this.#perUnit(price, units);
        const hasSubscription = (offer.sellingPlanAllocations?.length ?? 0) > 0;
        const showSubscriptionUi = this.#hasProtocolSellingPlans();
        const frequency =
          showSubscriptionUi && this.#state.purchaseMode === 'subscription' && allocation
            ? allocation.optionName || allocation.name
            : hasSubscription
              ? '—'
              : '—';

        return `
          <tr>
            <td>${this.#escape(this.#normalizeOfferLabel(offer.label || offer.title))}</td>
            <td>${units > 0 ? units : '—'}</td>
            <td>${this.#escape(offer.duration || '—')}</td>
            <td>${this.#escape(this.#format(price))}</td>
            <td>${perUnit ? this.#escape(this.#format(perUnit)) : '—'}</td>
            <td>${savings ? `${savings.percent}%` : '—'}</td>
            <td>${hasSubscription ? 'Sim' : 'Não'}</td>
            <td>${this.#escape(frequency)}</td>
            <td>${offer.available ? 'Disponível' : 'Esgotado'}</td>
          </tr>
        `;
      })
      .join('');

    body.innerHTML = `
      <table class="purency-purchase__compare-table">
        <thead>
          <tr>
            <th>Tratamento</th>
            <th>Unidades</th>
            <th>Duração</th>
            <th>Total</th>
            <th>Por frasco</th>
            <th>Economia</th>
            <th>Assinatura</th>
            <th>Frequência</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  #renderCta() {
    const cta = this.querySelector('[data-purency-purchase-cta]');
    if (!(cta instanceof HTMLElement)) return;

    const label = this.#ctaLabel();
    const textEl = cta.querySelector('.purency-purchase__cta-text');
    if (textEl instanceof HTMLElement) {
      textEl.textContent = label;
    } else {
      cta.textContent = label;
    }

    const url = this.#getSelectedCheckoutUrl();
    const canCheckout = this.#externalCheckout.enabled ? Boolean(url) : this.#state.available;

    if (cta instanceof HTMLAnchorElement) {
      cta.href = url || '#';
      if (canCheckout) {
        cta.removeAttribute('aria-disabled');
        cta.removeAttribute('tabindex');
      } else {
        cta.setAttribute('aria-disabled', 'true');
        cta.setAttribute('tabindex', '-1');
      }
      return;
    }

    if (cta instanceof HTMLButtonElement) {
      if (!this.#externalCheckout.enabled) {
        cta.type = 'submit';
      }
      cta.disabled = !canCheckout;
    }
  }

  #renderDisclaimer() {
    const disclaimer = this.querySelector('[data-purency-purchase-subscription-disclaimer]');
    if (!(disclaimer instanceof HTMLElement)) return;
    disclaimer.hidden = !this.#shouldShowSubscriptionAside();
  }

  #announce() {
    const live = this.querySelector('[data-purency-purchase-live]');
    if (!(live instanceof HTMLElement)) return;
    const offer = this.#offers[this.#state.selectedOfferIndex];
    if (!offer) return;
    live.textContent = `${this.#normalizeOfferLabel(offer.label || offer.title)}: ${this.#format(this.#state.price)}. ${this.#ctaLabel()}`;
  }

  /** @param {string} value */
  #normalizeOfferLabel(value) {
    const trimmed = String(value || '').trim();
    if (trimmed === 'Experiência Inicial' || trimmed === 'Experiencia Inicial') {
      return 'Tratamento Inicial';
    }
    return value;
  }

  /** @param {string} value */
  #escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('purency-product-purchase')) {
  customElements.define('purency-product-purchase', PurencyProductPurchase);
}
